import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, IllustrativeIcon, InlineLink, StandAloneLink, Txt } from '@vst/beam';
import { Phone } from '@vst/beam-icons/icons';
import { Caution } from '@vst/beam-icons/illustrative-icons';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { SESSION_STORAGE_VARIABLES } from '@app/services/storageManager';
import { selectNetwork } from '@app/redux';

import styles from './styles.module.scss';

type RunLimitNotifierProps = {
  navigateToDiagnosticReport: (e: React.MouseEvent<HTMLElement>) => void;
};

export const RunLimitNotifier = ({ navigateToDiagnosticReport }: RunLimitNotifierProps) => {
  const { t } = useTranslation('Network', { keyPrefix: 'diagnostic' });

  const runDate = useLastRunDate();

  const phoneHref = `tel:${t('callCustomerSupportPhoneNumber')}`;

  return (
    <div className={styles['card']}>
      <div className={styles['content']}>
        <IllustrativeIcon
          data-cy="CheckMark-Icon"
          name="CheckMark"
          icon={Caution}
          color="warning_400"
          width={100}
          height={100}
        />
        <div className={styles['titleAndDescription']}>
          <Txt variant="heading5" mb="8px" data-cy="run-diagnostic-title">
            {t('runLimitIssue.title')}
          </Txt>
          <Txt variant="bodySmallRegular" color="subtle" data-cy="run-diagnostic-description">
            {t('runLimitIssue.description')}
          </Txt>
        </div>

        <InlineLink href={phoneHref} style={{ maxWidth: '400px', width: '100%' }}>
          <Button
            variant="primary"
            fluid
            data-cy="run-diagnostic-button"
            style={{ width: '100%' }}
            icon={Phone}
            iconPos="left"
          >
            {t('buttons.callCare')}
          </Button>
        </InlineLink>

        <div className={styles['dateAndLink']}>
          <Txt component="span" variant="smallRegular" data-cy="run-date">
            {runDate}
          </Txt>
          <StandAloneLink
            variant="primary"
            linkSize="small"
            href=""
            onClick={navigateToDiagnosticReport}
            data-cy="go-to-full-report"
            underline
          >
            {t('linkToDiagnosticReport')}
          </StandAloneLink>
        </div>
      </div>
    </div>
  );
};

const useLastRunDate = () => {
  const { runDate: storeRunDate } = useSelector(selectNetwork);

  const runDate = useMemo(() => {
    if (storeRunDate) {
      return storeRunDate;
    }
    const runDateFromStorage = JSON.parse(
      localStorage.getItem(SESSION_STORAGE_VARIABLES.DIAGNOSTIC_RUNS_TIMESTAMPS) ?? '[]'
    );

    const lastTimeStampFromStorage = runDateFromStorage[runDateFromStorage.length - 1];

    if (lastTimeStampFromStorage) {
      return format(lastTimeStampFromStorage, 'MMM do HH:mm a zzz');
    }
    return null;
  }, [storeRunDate]);

  return runDate;
};
